/**
 * Marketing attribution capture.
 *
 * Two-channel capture (whichever returns data first):
 *   1) URL params on iframe src (works only if WP embed forwards them — currently no)
 *   2) postMessage to window.parent (works with any static iframe + small WP footer script)
 *
 * Captured data is stored in sessionStorage and survives the multi-step form.
 * Sent with the order POST and stored on Order in the backend.
 */

const ATTRIBUTION_KEYS = [
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "ttclid",
  "msclkid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const STORAGE_KEY = "pl_attribution";
const STORAGE_TIMESTAMP_KEY = "pl_attribution_ts";
const STORAGE_TTL_MS = 60 * 60 * 1000; // 1 hour — anything older counts as expired
const REQUEST_TYPE = "pl-attribution-request";
const RESPONSE_TYPE = "pl-attribution-response";

// Origins we trust for postMessage responses. Add more if you embed the
// iframe on additional domains.
const ALLOWED_PARENT_ORIGINS: ReadonlyArray<string | RegExp> = [
  "https://pramoguloftas.lt",
  "https://www.pramoguloftas.lt",
  // Allow any *.pramoguloftas.lt subdomain (staging, etc.)
  /^https:\/\/[a-z0-9-]+\.pramoguloftas\.lt$/,
  // Local dev — comment out for prod hardening if desired
  "http://localhost",
  "http://localhost:8080",
];

export type AttributionData = Partial<Record<(typeof ATTRIBUTION_KEYS)[number], string>> & {
  referrer?: string;
  landing_page?: string;
};

let listenerAttached = false;

function isOriginAllowed(origin: string): boolean {
  return ALLOWED_PARENT_ORIGINS.some((allowed) =>
    typeof allowed === "string" ? origin === allowed : allowed.test(origin)
  );
}

function readFromStorage(): AttributionData | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    // Expire stale data — protects against stored attribution from
    // a previous test or earlier site visit in the same tab.
    const tsRaw = sessionStorage.getItem(STORAGE_TIMESTAMP_KEY);
    if (tsRaw) {
      const ts = parseInt(tsRaw, 10);
      if (!Number.isNaN(ts) && Date.now() - ts > STORAGE_TTL_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
        return null;
      }
    }

    return JSON.parse(raw) as AttributionData;
  } catch {
    return null;
  }
}

function writeToStorage(data: AttributionData): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    sessionStorage.setItem(STORAGE_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // sessionStorage may be blocked — caller still gets the in-memory return value
  }
}

/**
 * Clears stored attribution. Call after a successful order submit so a
 * subsequent order in the same tab starts fresh and doesn't inherit stale
 * tracking data from the previous purchase journey.
 */
export function clearAttribution(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
  } catch {
    // ignore
  }
}

function captureFromUrl(): AttributionData {
  const params = new URLSearchParams(window.location.search);
  const data: AttributionData = {};

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) data[key] = value.slice(0, 500);
  });

  // Intentionally NOT capturing document.referrer here.
  // In an embedded iframe, document.referrer is always the parent page
  // (e.g. pramoguloftas.lt/...), which would falsely classify direct
  // visits as "referral". The real referrer (where the user came from
  // BEFORE landing on the parent) is sent by the parent via postMessage,
  // and the parent script filters out same-origin referrers itself.
  const landing = params.get("landing_page");
  if (landing) {
    data.landing_page = landing.slice(0, 2000);
  }

  return data;
}

function mergeFromParent(payload: any): void {
  const current = readFromStorage() || {};
  const incoming: AttributionData = {};

  if (payload && typeof payload.params === "object" && payload.params !== null) {
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = payload.params[key];
      if (typeof value === "string" && value) {
        incoming[key] = value.slice(0, 500);
      }
    });
  }
  if (typeof payload?.referrer === "string" && payload.referrer) {
    incoming.referrer = payload.referrer.slice(0, 2000);
  }
  if (typeof payload?.landing_page === "string" && payload.landing_page) {
    incoming.landing_page = payload.landing_page.slice(0, 2000);
  }

  // Parent wins for landing_page (it knows the full parent URL).
  // For everything else, prefer existing value; fill gaps from parent.
  const merged: AttributionData = { ...incoming, ...current };
  if (incoming.landing_page) merged.landing_page = incoming.landing_page;

  writeToStorage(merged);
}

function setupParentListener(): void {
  if (listenerAttached) return;
  listenerAttached = true;

  window.addEventListener("message", (event) => {
    if (!event.data || event.data.type !== RESPONSE_TYPE) return;
    if (!isOriginAllowed(event.origin)) return;
    mergeFromParent(event.data);
  });
}

function requestFromParent(): void {
  if (!window.parent || window.parent === window) return;
  try {
    // Targeted "*" because we don't know parent origin upfront; the parent
    // script validates the iframe origin before responding.
    window.parent.postMessage({ type: REQUEST_TYPE }, "*");
  } catch {
    // Cross-origin throws on some old browsers — ignore
  }
}

/**
 * Captures attribution from URL synchronously and asks the parent for any
 * additional data via postMessage. Call once on app mount.
 */
export function captureAttribution(): AttributionData {
  let data = readFromStorage();
  if (!data) {
    data = captureFromUrl();
    writeToStorage(data);
  }

  setupParentListener();
  // Ask parent immediately + retry once (in case parent script loads after iframe)
  requestFromParent();
  setTimeout(requestFromParent, 500);
  setTimeout(requestFromParent, 2000);

  return data;
}

/**
 * Returns the latest attribution data. By the time the user submits the form,
 * any parent postMessage response has been merged into storage.
 */
export function getAttribution(): AttributionData {
  return readFromStorage() || captureFromUrl();
}

// Internal attribution fields that the backend uses but should not leak into
// the parent's redirect URL — landing_page is just the redirect target itself
// and referrer is captured independently by the parent page.
const REDIRECT_EXCLUDED_KEYS: ReadonlySet<string> = new Set([
  "landing_page",
  "referrer",
]);

/**
 * Builds a query string of attribution params for forwarding to the parent
 * redirect URL after a successful order. Only includes non-empty values and
 * skips internal-only fields that would clutter the visible URL.
 */
export function attributionToQueryString(data: AttributionData): string {
  const params = new URLSearchParams();
  Object.entries(data).forEach(([key, value]) => {
    if (value && !REDIRECT_EXCLUDED_KEYS.has(key)) {
      params.set(key, value);
    }
  });
  return params.toString();
}
